import {
  cn,
  Dialog,
  RadioGroup,
  RadioItem,
  Switch,
  Text,
} from '@risksmart-app/atomic-ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ToolbarLabelRevealMode } from '../useObjectToolbarSettingsStore';
import { useObjectToolbarSettingsStore } from '../useObjectToolbarSettingsStore';
import { LabelRevealPreview } from './LabelRevealPreview';

/**
 * Cooldown (ms) the "Show action labels" switch stays disabled after a toggle.
 * Gating clicks here stops the switch being spam-toggled — the accepted toggle
 * commits to the store immediately, only a *second* click within the window is
 * blocked.
 */
const SHOW_LABELS_THROTTLE_MS = 1000;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The reveal mode currently in effect when the user hasn't made an explicit
   * choice (the connected toolbar's `DEFAULT_LABEL_REVEAL_MODE`). Selects the
   * highlighted option so the picker reflects what's actually live.
   */
  defaultRevealMode: ToolbarLabelRevealMode;
}

const REVEAL_MODES: ToolbarLabelRevealMode[] = ['tooltip', 'slideout'];

/**
 * "Toolbar settings" modal — a per-user, per-browser display preference panel
 * reachable from the overflow "Edit" group on every object toolbar. Changes
 * apply live via {@link useObjectToolbarSettingsStore} (session-scoped, not yet
 * persisted). There is no save/cancel step because these are cosmetic display
 * toggles.
 */
export const ToolbarSettingsDialog = ({
  open,
  onOpenChange,
  defaultRevealMode,
}: Props) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'objectToolbar.settingsDialog',
  });
  const revealOptions = REVEAL_MODES.map((mode) => ({
    mode,
    label: t(`revealOptions.${mode}.label`),
    description: t(`revealOptions.${mode}.description`),
  }));
  const showActionLabels = useObjectToolbarSettingsStore(
    (s) => s.showActionLabels
  );
  const labelRevealMode = useObjectToolbarSettingsStore(
    (s) => s.labelRevealMode
  );
  const setShowActionLabels = useObjectToolbarSettingsStore(
    (s) => s.setShowActionLabels
  );
  const setLabelRevealMode = useObjectToolbarSettingsStore(
    (s) => s.setLabelRevealMode
  );

  // The user's explicit choice, or the flag-resolved default when unset.
  const selectedMode = labelRevealMode ?? defaultRevealMode;

  // Disable the switch for a beat after each toggle so it can't be spam-clicked.
  // The opacity fade telegraphs the cooldown.
  const [isThrottled, setIsThrottled] = useState(false);
  const throttleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => () => clearTimeout(throttleTimer.current), []);

  const handleShowActionLabelsChange = (checked: boolean) => {
    setShowActionLabels(checked);
    setIsThrottled(true);
    clearTimeout(throttleTimer.current);
    throttleTimer.current = setTimeout(
      () => setIsThrottled(false),
      SHOW_LABELS_THROTTLE_MS
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size={'lg'}>
      <Dialog.Header title={t('title')} description={t('description')} />
      <Dialog.Body className={cn('flex flex-col gap-6')}>
        <div className={cn('flex items-center gap-3')}>
          <div className={cn('flex-1')}>
            <Text preset={'body-lg'} className={cn('font-medium')}>
              {t('showActionLabels')}
            </Text>
            <Text preset={'body-md'} variant={'muted'}>
              {t('showActionLabelsDescription')}
            </Text>
          </div>
          <Switch
            checked={showActionLabels}
            onCheckedChange={handleShowActionLabelsChange}
            disabled={isThrottled}
            size={'md'}
            aria-label={t('showActionLabels')}
            className={cn(
              'transition-opacity duration-300',
              isThrottled && 'opacity-50'
            )}
          />
        </div>

        {/* Greyed out when labels are hidden. */}
        <div
          className={cn(
            'flex flex-col gap-2',
            !showActionLabels && 'pointer-events-none opacity-50'
          )}
        >
          <Text preset={'body-lg'} className={cn('font-medium')}>
            {t('labelStyle')}
          </Text>
          <RadioGroup
            value={selectedMode}
            onValueChange={(value) =>
              setLabelRevealMode(value as ToolbarLabelRevealMode)
            }
            aria-label={t('labelStyle')}
            aria-disabled={!showActionLabels}
            className={cn('flex flex-row gap-3')}
          >
            {revealOptions.map((option) => {
              const isSelected = selectedMode === option.mode;

              return (
                <label
                  key={option.mode}
                  className={cn(
                    'group/tile flex flex-1 cursor-pointer flex-col gap-3 rounded-lg border bg-neutral p-5',
                    // Subtle hover: border fades to/from secondary (transition on
                    // hover-out). `group/tile` also drives the preview's looping
                    // reveal animation on hover (see LabelRevealPreview).
                    'transition-colors duration-200 hover:border-primary',
                    isSelected ? 'border-secondary' : 'border-neutral-border'
                  )}
                >
                  <div className={cn('flex items-center gap-2')}>
                    <RadioItem
                      value={option.mode}
                      disabled={!showActionLabels}
                    />
                    <Text preset={'body-lg'} className={cn('font-medium')}>
                      {option.label}
                    </Text>
                  </div>
                  <div
                    className={cn(
                      'rounded-md border border-neutral-border-minimal bg-neutral-minimal p-3'
                    )}
                  >
                    <LabelRevealPreview mode={option.mode} />
                  </div>
                  <Text preset={'body-md'} variant={'muted'}>
                    {option.description}
                  </Text>
                </label>
              );
            })}
          </RadioGroup>
        </div>
      </Dialog.Body>
    </Dialog>
  );
};
